import { useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogContent, useMediaQuery, useTheme } from "@mui/material";

import type { Job } from "../../../../models";
import { GeoJSON } from "../../../../utilities/utils";
import JobMap from "../../../helpers/JobMap/JobMap";

export interface DashboardMapModalProps {
	open: boolean;
	onClose: () => void;
	selectedJobs: Job[];
}

export function DashboardMapModal({ open, onClose, selectedJobs }: DashboardMapModalProps) {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [jobsWithGeo, setJobsWithGeo] = useState<Job[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open || selectedJobs.length === 0) {
			setJobsWithGeo([]);
			return;
		}

		let cancelled = false;

		async function fetchGeodata() {
			setLoading(true);
			try {
				const results = await Promise.all(
					selectedJobs.map(async (job) => {
						try {
							const [boundary, points, zones] = await Promise.all([
								GeoJSON.getBoundary(job),
								GeoJSON.getPoints(job),
								GeoJSON.getZones(job),
							]);

							if (!boundary && !points && !zones) return null;

							return {
								...job,
								files: {
									...job.files,
									boundaryGeoJSON: boundary ?? undefined,
									pointsGeoJSON: points ?? undefined,
									zonesGeoJSON: zones ?? undefined,
								},
							} as Job;
						} catch (err) {
							console.warn(`Failed to load geo data for job ${job.id}:`, err);
							return null;
						}
					}),
				);

				if (!cancelled) {
					setJobsWithGeo(results.filter((j): j is Job => j !== null));
				}
			} catch (err) {
				console.error("Failed to fetch geodata:", err);
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}

		fetchGeodata();
		return () => {
			cancelled = true;
		};
	}, [open, selectedJobs]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xl"
			fullWidth
			fullScreen={fullScreen}
			PaperProps={{ sx: { height: fullScreen ? "100vh" : "90vh", maxHeight: fullScreen ? "100vh" : "90vh" } }}
		>
			<DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
				{loading ? (
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
						<CircularProgress />
					</div>
				) : (
					<JobMap jobs={jobsWithGeo} colorMode="per-job" showToolbar onClose={onClose} height="100%" showEmptyState />
				)}
			</DialogContent>
		</Dialog>
	);
}
